import { RandomId } from "@swivl-mono/helpers";
import { LLMNodeMode, NodeData_LLM, NodeInterface, NodeType, PromptStackItem, PromptStackItemType, UserVarKey, VarKey } from "@swivl-mono/types";
import { RadioButtons, Spinner } from "@swivl-mono/ui";
import { useEffect } from "react";
import { FaCheck, FaCopy } from "react-icons/fa";

import { PromptStack } from "../../../../../Components/PromptStack/PromptStack";
import { AIModel } from "../../../../../Models/AI/AI.Model";
import TabNodeLayoutData, { TabNodeLayoutTabInterface } from "../../NodeLayouts/TabNodeLayoutData";
import InspectorFooter from "../../SidePanel/Inspector/components/InspectorFooter";
import InspectorNodeHeader from "../../SidePanel/Inspector/components/InspectorNodeHeader";
import NodePanel from "../NodePanel";

const LLMNode = {
  Type: NodeType.LLM,
  Empty: function () {
    return {
      id: RandomId(6),
      type: NodeType.LLM,
      mode: LLMNodeMode.TEXT,
      prompts: [
        {
          id: "40nJazc6",
          type: "prompt",
          promptSlug: "n5jcSSDn"
        },
        {
          id: "U9hZjwwZ",
          type: "previous_messages",
          numberOfMessagesToLoad: 10
        },
        {
          id: "VQ0TYjWB",
          type: "prompt",
          promptSlug: "tUxuZzjg"
        },
        {
          id: "NjyTlRjc",
          type: "prompt",
          promptSlug: "fZ2CIq0j"
        },
        {
          id: "7w7vxgSe",
          type: "prompt",
          promptSlug: "pvAJWRY0"
        },
        {
          id: "GJ4izCvZ",
          text: "",
          type: "text"
        }
      ],
      goto: "",
      numberOfTokensToReturn: 125,
      successGoto: "",
      errorGoto: ""
    } as NodeData_LLM;
  },

  isInvald: function (node: NodeInterface) {
    const llmNode = node as NodeData_LLM;

    // Skip validation for text mode (maintains backward compatibility)
    if (llmNode.mode === LLMNodeMode.TEXT || !llmNode.mode) {
      return undefined;
    }

    // For structured/search modes, validate that a prompt is selected
    if (!llmNode.selectedPromptSlug) {
      const modeText = llmNode.mode === LLMNodeMode.STRUCTURED ? "structured" : "search";
      return { node: node, issue: `${modeText} mode requires prompt selection` };
    }

    // Note: Schema/mapping validation happens in inspector panel since we don't have prompt data here
    // Runtime errors will flow to errorGoto path during execution
    return undefined;
  },

  PlumbData: function (node: NodeInterface) {
    const llmNode = node as NodeData_LLM;

    // Generate body text based on mode
    const text: any = [];

    if (llmNode.mode === LLMNodeMode.TEXT || !llmNode.mode) {
      // Text mode: show prompt stack
      if (llmNode.prompts) {
        llmNode.prompts.forEach((prompt: any) => {
          if (prompt.type === PromptStackItemType.TEXT) {
            text.push("• " + (prompt.text.length > 33 ? prompt.text.substring(0, 30) + "..." : prompt.text));
          } else if (prompt.type === PromptStackItemType.PREVIOUS_MESSAGES) {
            text.push(`• Load ${prompt.numberOfMessagesToLoad} Messages`);
          } else {
            text.push("• Prompt" + AIModel.nameForPrompt(prompt.promptSlug));
          }
        });
      }
    } else {
      // Structured/Search mode: show mode type and prompt slug if selected
      const modeLabel = llmNode.mode === LLMNodeMode.STRUCTURED ? "Structured" : "Search";
      text.push(modeLabel);

      if (llmNode.selectedPromptSlug) {
        text.push(llmNode.selectedPromptSlug);
      }
    }

    // Create tabs for routing - different tabs based on whether success/error routing is configured
    const tabs: TabNodeLayoutTabInterface[] = [];

    // For new enhanced modes, show success/error routing
    if (llmNode.mode === LLMNodeMode.STRUCTURED || llmNode.mode === LLMNodeMode.SEARCH) {
      tabs.push(
        {
          id: "successGoto",
          source: "successGoto",
          target: llmNode.successGoto,
          title: "Success"
        },
        {
          id: "errorGoto",
          source: "errorGoto",
          target: llmNode.errorGoto,
          title: "Error"
        }
      );
    } else {
      // Text mode uses simple goto for backward compatibility
      tabs.push({
        id: "goto",
        source: "goto",
        target: llmNode.goto
      });
    }

    return TabNodeLayoutData({
      node: node as any,
      title: "LLM",
      body: text.join("\n"),
      baseWidth: 250,
      bodyHeight: text.length * 20 + 20,
      tabs: tabs
    });
  },

  // }
  InspectorPanel: function LLMNodeInspectorPanel(props: any) {
    const prompts = AIModel.useState((s) => s.prompts);

    // eslint-disable-next-line react/jsx-pascal-case
    useEffect(() => {
      AIModel.loadPrompts();
    });

    // Trigger node update when prompts finish loading to refresh PlumbData display
    useEffect(() => {
      if (prompts && props.node && (props.node.mode === LLMNodeMode.STRUCTURED || props.node.mode === LLMNodeMode.SEARCH)) {
        // Force a node update to refresh the canvas display when prompts are loaded
        // This is needed because PlumbData shows "Loading..." until prompts are available
        props.updateNode(props.node, props.node, false);
      }
    }, [prompts, props.node?.selectedPromptSlug]);

    return <LLMNode.InternalInspectorPanel {...props} prompts={prompts} />;
  },

  InternalInspectorPanel: class LLMNodeInspectorPanelClass extends NodePanel {
    constructor(props: any) {
      super(props);
      this.state = {
        copiedStates: {} // Track copy success states for different JSON displays
      };
      this.updatePrompts = this.updatePrompts.bind(this);
      this.updateMode = this.updateMode.bind(this);
      this.ensureBackwardCompatibility = this.ensureBackwardCompatibility.bind(this);
      this.handleModeChange = this.handleModeChange.bind(this);
      this.handlePromptSelection = this.handlePromptSelection.bind(this);
      this.renderStructuredModeConfig = this.renderStructuredModeConfig.bind(this);
      this.renderSearchModeConfig = this.renderSearchModeConfig.bind(this);
      this.getPromptsByType = this.getPromptsByType.bind(this);
      this.formatJSON = this.formatJSON.bind(this);
      this.validateJSON = this.validateJSON.bind(this);
      this.handleCopyToClipboard = this.handleCopyToClipboard.bind(this);
      this.getCopyContent = this.getCopyContent.bind(this);
    }

    updatePrompts(prompts: PromptStackItem[]) {
      const node = JSON.parse(JSON.stringify(this.props.node));
      node.prompts = prompts;
      this.props.updateNode(node, this.props.node, true);
    }

    updateMode(mode: LLMNodeMode) {
      const node = JSON.parse(JSON.stringify(this.props.node));
      node.mode = mode;

      // Always clear selectedPromptSlug when switching modes to prevent wrong prompt data from showing
      delete node.selectedPromptSlug;

      this.props.updateNode(node, this.props.node, true);
    }

    ensureBackwardCompatibility(node: NodeData_LLM): NodeData_LLM {
      // Ensure existing nodes without mode get TEXT mode
      if (!node.mode) {
        node.mode = LLMNodeMode.TEXT;
      }
      return node;
    }

    handleModeChange(event: any) {
      const mode = event.target.value as LLMNodeMode;
      this.updateMode(mode);
    }

    handlePromptSelection(event: any) {
      const selectedPromptSlug = event.target.value;
      const prompts = (this.props as any).prompts;
      const selectedPrompt = prompts?.find((p: any) => p.slug === selectedPromptSlug);

      const node = JSON.parse(JSON.stringify(this.props.node));

      if (selectedPromptSlug && selectedPrompt) {
        // Set slug
        node.selectedPromptSlug = selectedPromptSlug;
      } else {
        // Clear field if no selection
        delete node.selectedPromptSlug;
      }

      this.props.updateNode(node, this.props.node, true);
    }

    getPromptsByType(type: string) {
      const prompts = (this.props as any).prompts;
      if (!prompts) return [];
      return prompts.filter((prompt: any) => prompt.type === type);
    }

    formatPromptDisplayName(prompt: any): string {
      const baseName = prompt.name || prompt.slug;

      // Add inheritance indicators based on inheritance status
      switch (prompt.inheritance) {
        case "child":
          return `(child) ${baseName}`;
        case "none":
          return `(standalone) ${baseName}`;
        case "parent":
        default:
          return baseName; // No prefix for parent prompts
      }
    }

    sortPromptsAlphabetically(prompts: any[]): any[] {
      return [...prompts].sort((a, b) => {
        const nameA = this.formatPromptDisplayName(a);
        const nameB = this.formatPromptDisplayName(b);
        return nameA.localeCompare(nameB);
      });
    }

    formatJSON(jsonString: string): string {
      // First check if the JSON is valid (with variable replacement)
      const validation = this.validateJSON(jsonString);
      if (!validation.isValid) {
        return jsonString; // Return original if not valid
      }

      try {
        // Temporarily replace variables with quoted placeholders for parsing
        const tempJson = jsonString.replace(/#\*([^*]+)\*#/g, '"TEMP_VAR_$1"');
        const parsed = JSON.parse(tempJson);
        const formatted = JSON.stringify(parsed, null, 2);

        // Restore the original variables in the formatted output
        const restored = formatted.replace(/"TEMP_VAR_([^"]+)"/g, "#*$1*#");
        return restored;
      } catch {
        return jsonString; // Return original if formatting fails
      }
    }

    validateJSON(jsonString: string): { isValid: boolean; error?: string } {
      if (!jsonString || jsonString.trim() === "") {
        return { isValid: false, error: "JSON cannot be empty" };
      }

      try {
        // Replace Swivl variables with placeholder values for validation
        const jsonWithPlaceholders = this.replaceVariablesForValidation(jsonString);
        JSON.parse(jsonWithPlaceholders);
        return { isValid: true };
      } catch (error) {
        return { isValid: false, error: `Invalid JSON structure: ${error instanceof Error ? error.message : String(error)}` };
      }
    }

    validateSchema(schema: string): { isValid: boolean; error?: string; warnings?: string[] } {
      const jsonValidation = this.validateJSON(schema);
      if (!jsonValidation.isValid) {
        return jsonValidation;
      }

      try {
        // First parse the original schema to validate User variables BEFORE placeholder replacement
        const originalParsed = JSON.parse(schema);

        // Validate ALL variables in the original schema (User, Location, Unit, etc.)
        const allVariableValidation = this.validateAllVariablesInObject(originalParsed);
        if (!allVariableValidation.isValid) {
          return { isValid: false, error: allVariableValidation.error };
        }

        // Now replace variables with placeholders for structural validation
        const jsonWithPlaceholders = this.replaceVariablesForValidation(schema);
        const parsed = JSON.parse(jsonWithPlaceholders);

        const warnings: string[] = [];

        // Define required and optional fields for structured mode
        const requiredFields = ["model", "user_message", "schema"];
        const optionalFields = ["user_id", "response_id", "metadata", "temperature", "max_output_tokens"];
        const allValidFields = [...requiredFields, ...optionalFields];

        // Check for missing required fields
        const missingRequired = requiredFields.filter((field) => !parsed[field]);
        if (missingRequired.length > 0) {
          return {
            isValid: false,
            error: `Missing required fields: ${missingRequired.join(", ")}. OpenAI API call will likely fail.`
          };
        }

        // Check for extra fields that will be ignored
        const providedFields = Object.keys(parsed);
        const extraFields = providedFields.filter((field) => !allValidFields.includes(field));
        if (extraFields.length > 0) {
          warnings.push(`These fields will be ignored: ${extraFields.join(", ")}`);
        }

        // Validate model name format
        if (typeof parsed.model !== "string" || !parsed.model.startsWith("gpt-")) {
          return { isValid: false, error: 'Model must be a valid OpenAI model (e.g., "gpt-4.1")' };
        }

        // Validate schema field format and JSON Schema compliance
        if (parsed.schema) {
          let schemaToValidate;
          try {
            if (typeof parsed.schema === "string") {
              schemaToValidate = JSON.parse(parsed.schema);
            } else if (typeof parsed.schema === "object") {
              schemaToValidate = parsed.schema;
            } else {
              return { isValid: false, error: "Schema field must be a JSON object or JSON string" };
            }
          } catch (e) {
            return { isValid: false, error: "Schema field contains invalid JSON" };
          }

          // Validate JSON Schema compliance requirements
          const complianceValidation = this.validateJSONSchemaCompliance({ schema: schemaToValidate });
          if (!complianceValidation.isValid) {
            return { isValid: false, error: `Schema compliance error: ${complianceValidation.error}` };
          }
        }

        return { isValid: true, warnings: warnings.length > 0 ? warnings : undefined };
      } catch (error) {
        return { isValid: false, error: `Schema validation failed: ${error instanceof Error ? error.message : String(error)}` };
      }
    }

    validateSearchConfig(config: string): { isValid: boolean; error?: string; warnings?: string[] } {
      const jsonValidation = this.validateJSON(config);
      if (!jsonValidation.isValid) {
        return jsonValidation;
      }

      try {
        // First parse the original config to validate User variables BEFORE placeholder replacement
        const originalParsed = JSON.parse(config);

        // Validate ALL variables in the original search config (User, Location, Unit, etc.)
        const allVariableValidation = this.validateAllVariablesInObject(originalParsed);
        if (!allVariableValidation.isValid) {
          return { isValid: false, error: allVariableValidation.error };
        }

        // Now replace variables with placeholders for structural validation
        const jsonWithPlaceholders = this.replaceVariablesForValidation(config);
        const parsed = JSON.parse(jsonWithPlaceholders);

        const warnings: string[] = [];

        // Define required and optional fields for search mode
        const requiredFields = ["model", "user_message", "vector_store_id"];
        const optionalFields = ["user_id", "response_id", "metadata", "temperature", "max_output_tokens"];
        const allValidFields = [...requiredFields, ...optionalFields];

        // Check for missing required fields
        const missingRequired = requiredFields.filter((field) => !parsed[field]);
        if (missingRequired.length > 0) {
          return {
            isValid: false,
            error: `Missing required fields: ${missingRequired.join(", ")}. OpenAI API call will likely fail.`
          };
        }

        // Check for extra fields that will be ignored
        const providedFields = Object.keys(parsed);
        const extraFields = providedFields.filter((field) => !allValidFields.includes(field));
        if (extraFields.length > 0) {
          warnings.push(`These fields will be ignored: ${extraFields.join(", ")}`);
        }

        // Validate model name format
        if (typeof parsed.model !== "string" || !parsed.model.startsWith("gpt-")) {
          return { isValid: false, error: 'Model must be a valid OpenAI model (e.g., "gpt-4.1")' };
        }

        // Validate vector_store_id format (CSV string with at least one valid ID)
        if (parsed.vector_store_id) {
          const vectorStoreValidation = this.validateVectorStoreIds(parsed.vector_store_id);
          if (!vectorStoreValidation.isValid) {
            return { isValid: false, error: vectorStoreValidation.error };
          }
        }

        return { isValid: true, warnings: warnings.length > 0 ? warnings : undefined };
      } catch (error) {
        return { isValid: false, error: `Search config validation failed: ${error instanceof Error ? error.message : String(error)}` };
      }
    }

    validateVectorStoreIds(vectorStoreId: string): { isValid: boolean; error?: string } {
      if (!vectorStoreId || typeof vectorStoreId !== "string") {
        return { isValid: false, error: "vector_store_id is required and must be a string" };
      }

      // Simple comma splitting and trimming
      const ids = vectorStoreId
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id);

      if (ids.length === 0) {
        return { isValid: false, error: "vector_store_id must contain at least one valid vector store ID (comma-separated)" };
      }

      return { isValid: true };
    }

    validateJSONSchemaCompliance(schemaField: any): { isValid: boolean; error?: string } {
      try {
        // 1. Must have top-level "schema" key
        if (!schemaField.schema || typeof schemaField.schema !== "object") {
          return { isValid: false, error: 'Schema field must contain a "schema" key with an object value' };
        }

        const schema = schemaField.schema;

        // 2. First key must be "type" with value "object"
        if (schema.type !== "object") {
          return { isValid: false, error: 'Schema must have "type": "object" as the first property' };
        }

        // 3. Must have "properties" as an object
        if (!schema.properties || typeof schema.properties !== "object") {
          return { isValid: false, error: 'Schema must have a "properties" object' };
        }

        // 4. Validate each property in "properties"
        for (const [propName, propDef] of Object.entries(schema.properties)) {
          const prop = propDef as any;

          // Each property must have "type"
          if (!prop.type) {
            return { isValid: false, error: `Property "${propName}" must have a "type" field` };
          }

          // If it's an object type, it must follow the same rules
          if (prop.type === "object") {
            const nestedValidation = this.validateNestedObjectSchema(prop, propName);
            if (!nestedValidation.isValid) {
              return nestedValidation;
            }
          }

          // If it's an array with object items, validate the items schema
          if (prop.type === "array" && prop.items && prop.items.type === "object") {
            const itemsValidation = this.validateNestedObjectSchema(prop.items, `${propName}.items`);
            if (!itemsValidation.isValid) {
              return itemsValidation;
            }
          }
        }

        // 5. Must have "required" array listing all properties
        if (!Array.isArray(schema.required)) {
          return { isValid: false, error: 'Schema must have a "required" array' };
        }

        const allProperties = Object.keys(schema.properties);
        if (schema.required.length !== allProperties.length) {
          return { isValid: false, error: 'The "required" array must list ALL properties defined in "properties"' };
        }

        // Check that all required fields exist in properties
        for (const requiredField of schema.required) {
          if (!schema.properties[requiredField]) {
            return { isValid: false, error: `Required field "${requiredField}" is not defined in properties` };
          }
        }

        // Check that all properties are listed in required
        for (const propName of allProperties) {
          if (!schema.required.includes(propName)) {
            return { isValid: false, error: `Property "${propName}" must be listed in the "required" array` };
          }
        }

        // 6. Must have "additionalProperties": false
        if (schema.additionalProperties !== false) {
          return { isValid: false, error: 'Schema must have "additionalProperties": false' };
        }

        // 7. Check key order (type, properties, required, additionalProperties)
        const keys = Object.keys(schema);
        const expectedOrder = ["type", "properties", "required", "additionalProperties"];
        const requiredKeys = keys.filter((key) => expectedOrder.includes(key));

        for (let i = 0; i < requiredKeys.length; i++) {
          const expectedKey = expectedOrder[i];
          if (schema[expectedKey] !== undefined && requiredKeys[i] !== expectedKey) {
            return { isValid: false, error: `Schema keys must be in order: type, properties, required, additionalProperties` };
          }
        }

        return { isValid: true };
      } catch (error) {
        return { isValid: false, error: `Schema compliance validation failed: ${error instanceof Error ? error.message : String(error)}` };
      }
    }

    validateNestedObjectSchema(objectSchema: any, path: string): { isValid: boolean; error?: string } {
      // Nested objects must follow the same structure rules
      if (objectSchema.type !== "object") {
        return { isValid: true }; // Not an object, skip validation
      }

      if (!objectSchema.properties || typeof objectSchema.properties !== "object") {
        return { isValid: false, error: `Nested object at "${path}" must have a "properties" object` };
      }

      if (!Array.isArray(objectSchema.required)) {
        return { isValid: false, error: `Nested object at "${path}" must have a "required" array` };
      }

      if (objectSchema.additionalProperties !== false) {
        return { isValid: false, error: `Nested object at "${path}" must have "additionalProperties": false` };
      }

      // Validate nested properties recursively
      for (const [propName, propDef] of Object.entries(objectSchema.properties)) {
        const prop = propDef as any;

        if (prop.type === "object") {
          const nestedValidation = this.validateNestedObjectSchema(prop, `${path}.${propName}`);
          if (!nestedValidation.isValid) {
            return nestedValidation;
          }
        }

        if (prop.type === "array" && prop.items && prop.items.type === "object") {
          const itemsValidation = this.validateNestedObjectSchema(prop.items, `${path}.${propName}.items`);
          if (!itemsValidation.isValid) {
            return itemsValidation;
          }
        }
      }

      return { isValid: true };
    }

    validateMapping(mapping: string, schema?: string): { isValid: boolean; error?: string } {
      const jsonValidation = this.validateJSON(mapping);
      if (!jsonValidation.isValid) {
        return jsonValidation;
      }

      try {
        // Parse the original mapping to check variable formats
        const originalParsedMapping = JSON.parse(mapping);
        const jsonWithPlaceholders = this.replaceVariablesForValidation(mapping);
        const parsedMapping = JSON.parse(jsonWithPlaceholders);

        // Validate that mapping is an object
        if (typeof parsedMapping !== "object" || Array.isArray(parsedMapping)) {
          return { isValid: false, error: "Mapping must be a JSON object" };
        }

        // Validate variable name format and check for User variables only
        const invalidVariables: string[] = [];
        const nonExistentVariables: string[] = [];

        // Get all valid UserVarKey values as strings
        const validUserVarKeys = Object.values(UserVarKey) as string[];

        for (const [key, value] of Object.entries(originalParsedMapping)) {
          if (typeof value === "string" && value.includes("#*") && value.includes("*#")) {
            // Check if it follows Swivl variable format: #*Category:VariableName*#
            const variableRegex = /^#\*([^*:]+):([^*:]+)\*#$/;
            const match = value.match(variableRegex);

            if (!match) {
              return { isValid: false, error: `Invalid variable format "${value}". Use format: #*Category:VariableName*#` };
            }

            // Check if it's a User variable (for structured/search modes)
            const category = match[1];
            const variableName = match[2];

            if (category !== "User") {
              invalidVariables.push(`${value} (must be a User variable)`);
            } else {
              // Check if the User variable actually exists
              if (!validUserVarKeys.includes(variableName)) {
                nonExistentVariables.push(`${value} (User variable "${variableName}" does not exist)`);
              }
            }
          }
        }

        // If we found non-User variables, return error
        if (invalidVariables.length > 0) {
          return {
            isValid: false,
            error: `Only User variables are allowed in structured/search mode mapping. Invalid variables: ${invalidVariables.join(", ")}`
          };
        }

        // If we found non-existent User variables, return error
        if (nonExistentVariables.length > 0) {
          return {
            isValid: false,
            error: `The following User variables do not exist: ${nonExistentVariables.join(", ")}`
          };
        }

        // Check for circular references in variable mappings
        const circularCheck = this.detectCircularReferences(parsedMapping);
        if (!circularCheck.isValid) {
          return circularCheck;
        }

        // If schema is provided, validate mapping-to-schema consistency
        if (schema) {
          const schemaValidation = this.validateMappingConsistency(mapping, schema);
          if (!schemaValidation.isValid) {
            return schemaValidation;
          }
        }

        return { isValid: true };
      } catch (error) {
        return { isValid: false, error: `Mapping validation failed: ${error instanceof Error ? error.message : String(error)}` };
      }
    }

    validateMappingConsistency(mapping: string, schema: string): { isValid: boolean; error?: string } {
      try {
        const mappingWithPlaceholders = this.replaceVariablesForValidation(mapping);
        const schemaWithPlaceholders = this.replaceVariablesForValidation(schema);

        // Try to parse mapping first
        let parsedMapping;
        try {
          parsedMapping = JSON.parse(mappingWithPlaceholders);
        } catch (mappingError) {
          // If mapping parsing fails, this is actually a mapping error, not a consistency error
          return { isValid: false, error: `Invalid mapping JSON: ${mappingError instanceof Error ? mappingError.message : String(mappingError)}` };
        }

        // Try to parse schema
        let parsedSchema;
        try {
          parsedSchema = JSON.parse(schemaWithPlaceholders);
        } catch (schemaError) {
          // If schema parsing fails, indicate this is a schema error affecting consistency check
          return {
            isValid: false,
            error: `Cannot check consistency - invalid schema JSON: ${schemaError instanceof Error ? schemaError.message : String(schemaError)}`
          };
        }

        // Look for schema properties in multiple possible locations
        let schemaProperties = null;

        // Check for direct schema.properties (current format)
        if (parsedSchema.schema && parsedSchema.schema.properties) {
          schemaProperties = parsedSchema.schema.properties;
        }
        // Check for OpenAI Responses API format
        else if (parsedSchema.response_format && parsedSchema.response_format.json_schema) {
          schemaProperties = parsedSchema.response_format.json_schema.schema?.properties;
        }
        // Check for top-level properties (simple format)
        else if (parsedSchema.properties) {
          schemaProperties = parsedSchema.properties;
        }

        if (schemaProperties) {
          // Fields that are always available in OpenAI responses regardless of schema
          const alwaysAvailableFields = ["response_id"];

          for (const mappingKey of Object.keys(parsedMapping)) {
            // Skip validation for fields that are always available in OpenAI responses
            if (alwaysAvailableFields.includes(mappingKey)) {
              continue;
            }

            if (!schemaProperties[mappingKey]) {
              return {
                isValid: false,
                error: `Mapping field "${mappingKey}" does not exist in schema properties`
              };
            }
          }
        }

        return { isValid: true };
      } catch (error) {
        // Catch any other unexpected errors
        return { isValid: false, error: `Unexpected error during consistency check: ${error instanceof Error ? error.message : String(error)}` };
      }
    }

    /**
     * Detect circular references in variable mappings
     */
    detectCircularReferences(mapping: any, visited: Set<string> = new Set(), currentPath: string[] = []): { isValid: boolean; error?: string } {
      for (const [key, value] of Object.entries(mapping)) {
        const currentKey = currentPath.length > 0 ? `${currentPath.join(".")}.${key}` : key;

        if (visited.has(currentKey)) {
          return {
            isValid: false,
            error: `Circular reference detected in mapping: ${currentPath.join(" -> ")} -> ${key}`
          };
        }

        visited.add(currentKey);
        currentPath.push(key);

        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          const result = this.detectCircularReferences(value, visited, currentPath);
          if (!result.isValid) {
            return result;
          }
        }

        currentPath.pop();
      }

      return { isValid: true };
    }

    /**
     * Validates ALL variables in any object (schema or other JSON structures)
     * Validates User, Location, Unit, etc. variables against the full VarKey enum
     * Used for schema validation where any variable category is allowed
     */
    validateAllVariablesInObject(obj: any, path = ""): { isValid: boolean; error?: string } {
      const nonExistentVariables: string[] = [];

      // Get all valid VarKey values for validation (includes all categories)
      const validVarKeys = Object.values(VarKey).filter((v) => typeof v === "string") as string[];

      const findVariables = (value: any, currentPath: string) => {
        if (typeof value === "string" && value.includes("#*") && value.includes("*#")) {
          // Extract all variable patterns from the string
          const variableMatches = value.match(/#\*[^*]+:[^*]+\*#/g);
          if (variableMatches) {
            for (const variableMatch of variableMatches) {
              // Check if it follows Swivl variable format: #*Category:VariableName*#
              const variableRegex = /^#\*([^*:]+):([^*:]+)\*#$/;
              const match = variableMatch.match(variableRegex);

              if (match) {
                const category = match[1];
                const variableName = match[2];
                const fullVariableName = `${category}:${variableName}`;

                // Validate ALL variable categories against VarKey enum
                if (!validVarKeys.includes(fullVariableName)) {
                  const location = currentPath ? ` at ${currentPath}` : "";
                  const errorMsg = `Variable ${variableMatch}${location} does not exist. Please update or remove it.`;
                  nonExistentVariables.push(errorMsg);
                }
              }
            }
          }
        } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          // Recursively check object properties
          for (const [key, subValue] of Object.entries(value)) {
            const newPath = currentPath ? `${currentPath}.${key}` : key;
            findVariables(subValue, newPath);
          }
        } else if (Array.isArray(value)) {
          // Recursively check array elements
          value.forEach((item, index) => {
            const newPath = currentPath ? `${currentPath}[${index}]` : `[${index}]`;
            findVariables(item, newPath);
          });
        }
      };

      findVariables(obj, path);

      if (nonExistentVariables.length > 0) {
        return {
          isValid: false,
          error: nonExistentVariables.join(" ")
        };
      }

      return { isValid: true };
    }

    /**
     * Validates all variables in a text string (for prompt text validation)
     * Uses the same validation logic as validateAllVariablesInObject but for plain text
     */
    validateAllVariablesInText(text: string): { isValid: boolean; error?: string } {
      const nonExistentVariables: string[] = [];

      // Get all valid VarKey values for validation (includes all categories)
      const validVarKeys = Object.values(VarKey).filter((v) => typeof v === "string") as string[];

      // Extract all variable patterns from the text
      const variableMatches = text.match(/#\*[^*]+:[^*]+\*#/g);
      if (variableMatches) {
        for (const variableMatch of variableMatches) {
          // Check if it follows Swivl variable format: #*Category:VariableName*#
          const variableRegex = /^#\*([^*:]+):([^*:]+)\*#$/;
          const match = variableMatch.match(variableRegex);

          if (match) {
            const category = match[1];
            const variableName = match[2];
            const fullVariableName = `${category}:${variableName}`;

            // Validate ALL variable categories against VarKey enum
            if (!validVarKeys.includes(fullVariableName)) {
              const errorMsg = `Variable ${variableMatch} does not exist. Please update or remove it.`;
              nonExistentVariables.push(errorMsg);
            }
          }
        }
      }

      if (nonExistentVariables.length > 0) {
        return {
          isValid: false,
          error: nonExistentVariables.join(" ")
        };
      }

      return { isValid: true };
    }

    /**
     * Validates User variables in any object (schema or other JSON structures)
     * Only validates User variables for existence - ignores Location, Unit, etc. variables
     * Used for mapping validation where only User variables are allowed
     */
    validateUserVariablesInObject(obj: any, path = ""): { isValid: boolean; error?: string } {
      const nonExistentUserVariables: string[] = [];

      // Get all valid UserVarKey values for validation (matches backend approach)
      const validUserVarKeys = Object.values(UserVarKey).filter((v) => typeof v === "string") as string[];

      const findVariables = (value: any, currentPath: string) => {
        if (typeof value === "string" && value.includes("#*") && value.includes("*#")) {
          // Extract all variable patterns from the string
          const variableMatches = value.match(/#\*[^*]+:[^*]+\*#/g);
          if (variableMatches) {
            for (const variableMatch of variableMatches) {
              // Check if it follows Swivl variable format: #*Category:VariableName*#
              const variableRegex = /^#\*([^*:]+):([^*:]+)\*#$/;
              const match = variableMatch.match(variableRegex);

              if (match) {
                const category = match[1];
                const variableName = match[2];

                // Only validate User variables - ignore Location, Unit, etc. variables
                if (category === "User") {
                  // Check if the User variable actually exists using UserVarKey enum
                  if (!validUserVarKeys.includes(variableName)) {
                    const location = currentPath ? ` at ${currentPath}` : " in schema";
                    const errorMsg = `${variableMatch}${location} (User variable "${variableName}" does not exist)`;
                    nonExistentUserVariables.push(errorMsg);
                  }
                }
                // For non-User variables (Location, Unit, etc.): no validation, assume valid
              }
            }
          }
        } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          // Recursively check object properties
          for (const [key, subValue] of Object.entries(value)) {
            const newPath = currentPath ? `${currentPath}.${key}` : key;
            findVariables(subValue, newPath);
          }
        } else if (Array.isArray(value)) {
          // Recursively check array elements
          value.forEach((item, index) => {
            const newPath = currentPath ? `${currentPath}[${index}]` : `[${index}]`;
            findVariables(item, newPath);
          });
        }
      };

      findVariables(obj, path);

      if (nonExistentUserVariables.length > 0) {
        return {
          isValid: false,
          error: `The following User variables are invalid: ${nonExistentUserVariables.join(", ")}`
        };
      }

      return { isValid: true };
    }

    /**
     * Replaces Swivl variables with placeholder values to make JSON parseable for validation
     * @param jsonString - JSON string containing Swivl variables in format #*Variable:Key*#
     * @returns JSON string with variables replaced by placeholder values
     */
    replaceVariablesForValidation(jsonString: string): string {
      // Simple and robust approach: replace all Swivl variables with a basic placeholder
      // Uses same regex pattern as VarsHelper for consistency
      return jsonString.replace(/#\*(.*?)\*#/g, "PLACEHOLDER_VALUE");
    }

    getSelectedPrompt() {
      const prompts = (this.props as any).prompts;
      const node = this.props.node as NodeData_LLM;
      if (!prompts) return null;

      let selectedPrompt = null;

      // Slug-based lookup with inheritance support
      if (node.selectedPromptSlug) {
        selectedPrompt = prompts.find((prompt: any) => prompt.slug === node.selectedPromptSlug);
      }

      if (!selectedPrompt) return null;

      // Ensure the selected prompt matches the current mode
      const expectedType = node.mode === LLMNodeMode.STRUCTURED ? "structured" : node.mode === LLMNodeMode.SEARCH ? "search" : null;

      if (expectedType && selectedPrompt.type !== expectedType) {
        return null; // Wrong type for current mode
      }

      return selectedPrompt;
    }

    validateNodeConfiguration(): { isValid: boolean; errors: string[] } {
      const node = this.props.node as NodeData_LLM;
      const errors: string[] = [];

      // Skip validation for text mode (maintains backward compatibility)
      if (node.mode === LLMNodeMode.TEXT || !node.mode) {
        return { isValid: true, errors: [] };
      }

      // Required field validation for structured/search modes
      if (!node.selectedPromptSlug) {
        errors.push("Please select a prompt for structured/search mode");
      }

      const selectedPrompt = this.getSelectedPrompt();
      if (selectedPrompt) {
        // Validate text (system prompt)
        if (!selectedPrompt.text || selectedPrompt.text.trim() === "") {
          errors.push("Selected prompt must have text content (system prompt)");
        } else {
          // Validate variables in text
          const textVariableValidation = this.validateAllVariablesInText(selectedPrompt.text);
          if (!textVariableValidation.isValid) {
            errors.push(`System prompt: ${textVariableValidation.error}`);
          }
        }

        // Validate schema
        if (!selectedPrompt.schema || selectedPrompt.schema.trim() === "") {
          errors.push("Selected prompt must have a schema configuration");
        } else {
          const schemaValidation =
            node.mode === LLMNodeMode.SEARCH ? this.validateSearchConfig(selectedPrompt.schema) : this.validateSchema(selectedPrompt.schema);
          if (!schemaValidation.isValid) {
            errors.push(`Schema: ${schemaValidation.error}`);
          }
          // Note: Warnings are displayed in the schema section, not in overall validation
        }

        // Validate mapping
        if (!selectedPrompt.mapping || selectedPrompt.mapping.trim() === "") {
          errors.push("Selected prompt must have a mapping configuration");
        } else {
          const mappingValidation = this.validateMapping(selectedPrompt.mapping, selectedPrompt.schema);
          if (!mappingValidation.isValid) {
            errors.push(`Mapping: ${mappingValidation.error}`);
          }
        }
      }

      return { isValid: errors.length === 0, errors };
    }

    async handleCopyToClipboard(content: string, displayId: string) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(content);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement("textarea");
          textArea.value = content;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
        }

        // Show success feedback
        this.setState({
          copiedStates: {
            ...this.state.copiedStates,
            [displayId]: true
          }
        });

        // Clear success state after 2 seconds
        setTimeout(() => {
          this.setState({
            copiedStates: {
              ...this.state.copiedStates,
              [displayId]: false
            }
          });
        }, 2000);
      } catch (error) {
        // warn: clipboard denial is a user/browser-environment condition, not a bug
        console.warn("Failed to copy to clipboard:", error);
        // Could add error toast here if needed
      }
    }

    getCopyContent(jsonString: string, validation: { isValid: boolean; error?: string }, isSchema = false): string {
      if (!validation.isValid && validation.error) {
        return `⚠️ ${validation.error}\n\n${jsonString}`;
      }

      // Return formatted JSON if valid, otherwise return raw content
      return validation.isValid ? this.formatJSON(jsonString) : jsonString;
    }

    renderStructuredModeConfig() {
      const structuredPrompts = this.getPromptsByType("structured");
      const sortedPrompts = this.sortPromptsAlphabetically(structuredPrompts);
      const node = this.props.node as NodeData_LLM;
      const selectedPrompt = this.getSelectedPrompt();

      // Determine current value
      let currentValue = node.selectedPromptSlug || "";

      return (
        <div className="structured-mode-config">
          <label>Select Structured Prompt:</label>
          <select value={currentValue} onChange={this.handlePromptSelection} style={{ width: "100%", padding: "8px", marginBottom: "16px" }}>
            <option value="">-- Select a structured prompt --</option>
            {sortedPrompts.map((prompt: any) => (
              <option key={prompt.slug} value={prompt.slug}>
                {this.formatPromptDisplayName(prompt)}
              </option>
            ))}
          </select>

          {selectedPrompt && (
            <div>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "8px" }}>System Prompt:</label>
                {this.renderTextDisplay(selectedPrompt.text, `text-${selectedPrompt.id}`)}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "8px" }}>Schema:</label>
                {this.renderJSONDisplay(selectedPrompt.schema, "schema", `schema-${selectedPrompt.id}`)}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "8px" }}>Mapping:</label>
                {this.renderJSONDisplay(selectedPrompt.mapping, "mapping", `mapping-${selectedPrompt.id}`)}
              </div>
            </div>
          )}

          {structuredPrompts.length === 0 && (
            <div style={{ padding: "16px", backgroundColor: "#f5f5f5", borderRadius: "4px", color: "#666" }}>
              No structured prompts available. Create structured prompts in the prompt management section.
            </div>
          )}
        </div>
      );
    }

    renderSearchModeConfig() {
      const searchPrompts = this.getPromptsByType("search");
      const sortedPrompts = this.sortPromptsAlphabetically(searchPrompts);
      const node = this.props.node as NodeData_LLM;
      const selectedPrompt = this.getSelectedPrompt();

      // Determine current value
      let currentValue = node.selectedPromptSlug || "";

      return (
        <div className="search-mode-config">
          <label>Select Search Prompt:</label>
          <select value={currentValue} onChange={this.handlePromptSelection} style={{ width: "100%", padding: "8px", marginBottom: "16px" }}>
            <option value="">-- Select a search prompt --</option>
            {sortedPrompts.map((prompt: any) => (
              <option key={prompt.slug} value={prompt.slug}>
                {this.formatPromptDisplayName(prompt)}
              </option>
            ))}
          </select>

          {selectedPrompt && (
            <div>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "8px" }}>System Prompt:</label>
                {this.renderTextDisplay(selectedPrompt.text, `text-${selectedPrompt.id}`)}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "8px" }}>Search Config:</label>
                {this.renderJSONDisplay(selectedPrompt.schema, "search", `search-config-${selectedPrompt.id}`)}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontWeight: "bold", display: "block", marginBottom: "8px" }}>Mapping:</label>
                {this.renderJSONDisplay(selectedPrompt.mapping, "mapping", `mapping-${selectedPrompt.id}`)}
              </div>
            </div>
          )}

          {searchPrompts.length === 0 && (
            <div style={{ padding: "16px", backgroundColor: "#f5f5f5", borderRadius: "4px", color: "#666" }}>
              No search prompts available. Create search prompts in the prompt management section.
            </div>
          )}
        </div>
      );
    }

    renderTextDisplay(text: string, displayId = "") {
      if (!text) {
        return (
          <div
            style={{ padding: "8px", backgroundColor: "#f9f9f9", border: "1px solid #ddd", borderRadius: "4px", fontStyle: "italic", color: "#666" }}
          >
            No text provided
          </div>
        );
      }

      // Validate variables in text field
      const textValidation = this.validateAllVariablesInText(text);
      const isCopied = this.state.copiedStates[displayId];

      const containerStyle = {
        position: "relative" as const,
        marginBottom: textValidation.isValid ? "0" : "8px"
      };

      const copyButtonStyle = {
        position: "absolute" as const,
        top: "8px",
        right: "8px",
        background: isCopied ? "#4caf50" : "#ffffff",
        border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "4px 6px",
        cursor: "pointer",
        fontSize: "12px",
        color: isCopied ? "#ffffff" : "#666",
        transition: "all 0.2s ease",
        zIndex: 10
      };

      return (
        <div>
          {/* Show validation error if text contains invalid variables */}
          {!textValidation.isValid && (
            <div
              style={{
                padding: "8px",
                backgroundColor: "#ffebee",
                border: "1px solid #e57373",
                borderRadius: "4px",
                color: "#c62828",
                marginBottom: "8px"
              }}
            >
              ⚠️ {textValidation.error}
            </div>
          )}

          <div style={containerStyle}>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "4px",
                padding: "12px",
                fontFamily: "monospace",
                fontSize: "12px",
                whiteSpace: "pre-wrap",
                maxHeight: "300px",
                overflowY: "auto"
              }}
            >
              {text}
            </div>
            <button
              style={copyButtonStyle}
              onClick={() => this.handleCopyToClipboard(text, displayId)}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.background = isCopied ? "#4caf50" : "#f0f0f0";
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.background = isCopied ? "#4caf50" : "#ffffff";
              }}
            >
              {isCopied ? <FaCheck /> : <FaCopy />}
            </button>
          </div>
        </div>
      );
    }

    renderJSONDisplay(jsonString: string, type: "schema" | "search" | "mapping" = "mapping", displayId = "") {
      if (!jsonString) {
        return (
          <div
            style={{ padding: "8px", backgroundColor: "#f9f9f9", border: "1px solid #ddd", borderRadius: "4px", fontStyle: "italic", color: "#666" }}
          >
            No data provided
          </div>
        );
      }

      // Use appropriate validation based on type
      let validation;
      if (type === "schema") {
        validation = this.validateSchema(jsonString);
      } else if (type === "search") {
        validation = this.validateSearchConfig(jsonString);
      } else {
        validation = this.validateJSON(jsonString);
      }

      // For mapping validation, also check consistency with schema if available
      if (type === "mapping" && validation.isValid) {
        const selectedPrompt = this.getSelectedPrompt();
        if (selectedPrompt && selectedPrompt.schema) {
          // First check if the schema itself is valid before running consistency check
          const schemaValidation = this.validateJSON(selectedPrompt.schema);
          if (schemaValidation.isValid) {
            const consistencyValidation = this.validateMappingConsistency(jsonString, selectedPrompt.schema);
            if (!consistencyValidation.isValid) {
              validation = consistencyValidation; // Override with consistency error
            }
          }
          // If schema is invalid, we don't show consistency errors for the mapping
          // The schema error will be shown in the schema section
        }
      }

      const copyContent = this.getCopyContent(jsonString, validation, type === "schema");
      const isCopied = this.state.copiedStates[displayId];

      const containerStyle = {
        position: "relative" as const,
        marginBottom: validation.isValid ? "0" : "8px"
      };

      const copyButtonStyle = {
        position: "absolute" as const,
        top: "8px",
        right: "8px",
        background: isCopied ? "#4caf50" : "#ffffff",
        border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "4px 6px",
        cursor: "pointer",
        fontSize: "12px",
        color: isCopied ? "#ffffff" : "#666",
        transition: "all 0.2s ease",
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        gap: "4px"
      };

      if (!validation.isValid) {
        return (
          <div>
            <div
              style={{
                padding: "8px",
                backgroundColor: "#ffebee",
                border: "1px solid #e57373",
                borderRadius: "4px",
                color: "#c62828",
                marginBottom: "8px"
              }}
            >
              ⚠️ {validation.error}
            </div>
            <div style={containerStyle}>
              <button
                style={copyButtonStyle}
                onClick={() => this.handleCopyToClipboard(copyContent, displayId)}
                title="Copy error message and content"
              >
                {isCopied ? <FaCheck size={10} /> : <FaCopy size={10} />}
                {isCopied ? "Copied!" : "Copy"}
              </button>
              <pre
                style={{
                  padding: "8px",
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "12px",
                  overflow: "auto",
                  maxHeight: "200px"
                }}
              >
                {jsonString}
              </pre>
            </div>
          </div>
        );
      }

      const formattedJSON = this.formatJSON(jsonString);

      return (
        <div>
          {/* Show warnings if present for schema or search config validation */}
          {(type === "schema" || type === "search") &&
            validation.isValid &&
            (validation as any).warnings &&
            (validation as any).warnings.length > 0 && (
              <div
                style={{
                  padding: "8px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffeaa7",
                  borderRadius: "4px",
                  color: "#856404",
                  marginBottom: "8px"
                }}
              >
                <div style={{ fontWeight: "bold", marginBottom: "4px" }}>⚠️ {type === "schema" ? "Schema" : "Search Config"} Warnings:</div>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {((validation as any).warnings as string[]).map((warning, index) => (
                    <li key={index} style={{ marginBottom: "2px", fontSize: "12px" }}>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          <div style={containerStyle}>
            <button style={copyButtonStyle} onClick={() => this.handleCopyToClipboard(copyContent, displayId)} title="Copy formatted JSON">
              {isCopied ? <FaCheck size={10} /> : <FaCopy size={10} />}
              {isCopied ? "Copied!" : "Copy"}
            </button>
            <pre
              style={{
                padding: "8px",
                backgroundColor: "#f5f5f5",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "12px",
                overflow: "auto",
                maxHeight: "200px",
                fontFamily: "Monaco, 'Courier New', monospace"
              }}
            >
              {formattedJSON}
            </pre>
          </div>
        </div>
      );
    }

    render() {
      const node = this.ensureBackwardCompatibility(this.props.node as NodeData_LLM);
      const prompts = (this.props as any).prompts;
      const validationResult = this.validateNodeConfiguration();

      return (
        <div className="panel LLMNodePanel">
          <InspectorNodeHeader title="LLM" id={node.id} />

          {/* Mode Selection Radio Buttons */}
          <label>Mode:</label>
          <RadioButtons
            options={[
              { value: LLMNodeMode.TEXT, label: "Text response" },
              { value: LLMNodeMode.STRUCTURED, label: "Structured response" },
              { value: LLMNodeMode.SEARCH, label: "Search response" }
            ]}
            name="mode"
            value={node.mode}
            onChange={this.handleModeChange}
          />

          {/* Detailed Validation Warnings for Inspector Panel */}
          {!validationResult.isValid && validationResult.errors.length > 0 && (
            <div style={{ marginTop: "16px", marginBottom: "16px" }}>
              <div style={{ padding: "12px", backgroundColor: "#fff3cd", border: "1px solid #ffeaa7", borderRadius: "4px", color: "#856404" }}>
                <div style={{ fontWeight: "bold", marginBottom: "8px" }}>⚠️ Configuration Warnings:</div>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {validationResult.errors.map((error, index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      {error}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: "8px", fontSize: "12px", fontStyle: "italic" }}>
                  Note: You can save this node, but runtime errors will flow to the Error path.
                </div>
              </div>
            </div>
          )}

          {/* Conditional Content Based on Mode */}
          {node.mode === LLMNodeMode.TEXT && (
            <>{prompts ? <PromptStack prompts={prompts} items={node.prompts} stackUpdated={this.updatePrompts} /> : <Spinner />}</>
          )}

          {node.mode === LLMNodeMode.STRUCTURED && this.renderStructuredModeConfig()}

          {node.mode === LLMNodeMode.SEARCH && this.renderSearchModeConfig()}

          <InspectorFooter
            deleteNode={this.deleteNode}
            node={this.props.node}
            duplicateNode={this.props.duplicateNode}
            canDuplicate={this.props.canDuplicate}
          />
        </div>
      );
    }
  }
};
export default LLMNode;
